import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import BadgeUserType from 'src/components/Badge/BadgeUserType';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import logger from 'src/util/logger';

import AdminUserControls from '../AdminUserControls';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactElement} - AdminUserList component
 */
function AdminUserList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.log(error); },
    );
  };

  if (!props) {
    return <Loading />;
  }

  const { relay, viewer } = props;
  const { users } = viewer;

  const intl = useIntl();

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.page_title.label', defaultMessage: 'Enosi Admin - Users' });
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.breadcrumb.admin.label', defaultMessage: 'Admin' });
  const breadcrumbUsers = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.breadcrumb.users.label', defaultMessage: 'Users' });
  const headingUsers = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.heading.label', defaultMessage: 'Users' });

  // Table headers
  const headerStatus = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.status.label', defaultMessage: 'Status' });
  const headerType = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.type.label', defaultMessage: 'Type' });
  const headerUsername = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.username.label', defaultMessage: 'Username' });
  const headerEmail = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.email.label', defaultMessage: 'Email' });
  const headerExternalId = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.external_identifier.label', defaultMessage: 'Ext ID' });
  const headerExternalIdTitle = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.table.external_identifier.title', defaultMessage: 'External identifier' });

  // Load more button
  const loadMoreStateLoadMore = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const loadMoreStateNoMore = intl.formatMessage({ id: 'admin.admin_user.admin_user_list.load_more_button.state_no_more.label', defaultMessage: 'No more users to load' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbUsers },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{headingUsers}</h1>
        <div className="float-end mb-4">
          <AdminUserControls />
        </div>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{headerStatus}</th>
                <th scope="col">{headerType}</th>
                <th scope="col">{headerUsername}</th>
                <th scope="col">{headerEmail}</th>
                <th scope="col">
                  <abbr title={headerExternalIdTitle}>{headerExternalId}</abbr>
                </th>
              </tr>
            </thead>
            <tbody>
              {
                users && users.edges && users.edges.map((edge) => {
                  const { node: user } = edge;
                  const {
                    uuid, externalIdentifier, type, email, active,
                  } = user;

                  return (
                    <tr key={`admin-user-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <td>
                        <BadgeUserType type={type} />
                      </td>
                      <th scope="row" colSpan={3}>
                        <Link to={`/admin/users/${uuid}`}>
                          <div>{displayUserName(intl, user)}</div>
                          <div>{email}</div>
                        </Link>
                        <div>{`(${externalIdentifier})`}</div>
                      </th>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={loadMoreStateLoadMore}
            copyNoMore={loadMoreStateNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminUserList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminUserList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminUserList,
  {
    viewer: graphql`
      fragment AdminUserList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        users(
          first: $count
          after: $cursor
        ) @connection(key: "AdminUserList_users") {
          edges {
            cursor
            node {
              uuid
              externalIdentifier
              type
              email
              givenName
              familyName
              active {
                start
                finish
              }
            }
          }
          count
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.users;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminUserList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminUserList_viewer
      }
    }
  `,
  },
);
